<script>
  import { onDestroy } from 'svelte';
  import { API_BASE_URL } from '../utils/config';
  import { downloadFile } from '../utils/imageUtils';

  const styleOptions = [
    {
      id: '스튜디오 감성',
      label: '스튜디오 감성',
      examples: ['/cocker/감성1.png', '/cocker/감성2.png', '/cocker/감성3.png', '/cocker/감성4.png']
    },
    {
      id: '스튜디오 야외',
      label: '스튜디오 야외',
      examples: ['/cocker/야외1.png', '/cocker/야외2.png', '/cocker/야외3.png', '/cocker/야외4.png']
    },
    {
      id: '스튜디오 SNS',
      label: '스튜디오 SNS',
      examples: ['/cocker/SNS1.png', '/cocker/SNS2.png', '/cocker/SNS3.png', '/cocker/SNS4.png']
    },
    {
      id: '스튜디오 광고',
      label: '스튜디오 광고',
      examples: ['/cocker/광고1.png', '/cocker/광고2.png', '/cocker/광고3.png', '/cocker/광고4.png']
    },
    {
      id: '스튜디오 아트',
      label: '스튜디오 아트',
      examples: ['/cocker/아트1.png', '/cocker/아트2.png', '/cocker/아트3.png', '/cocker/아트4.png']
    }
  ];
  const genderOptions = ['', '남아', '여아', '중성화 남아', '중성화 여아'];
  const maxUploads = 10;
  const maxUploadBytes = 10 * 1024 * 1024;
  const allowedUploadExtensions = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);

  let authorized = false;
  let accessToken = '';
  let password = '';
  let accessError = '';
  let activeWorkspaceTab = 'create';
  let selectedStyle = styleOptions[0]?.id || '';
  let uploads = [];
  let fileInput;
  let submitError = '';
  let isSubmitting = false;
  let isPolling = false;
  let jobId = '';
  let progress = emptyProgress();
  let resultHistory = [];
  let historyBatches = [];
  let expandedHistoryIds = [];
  let expandedHistoryDateKeys = [];
  let isLoadingHistory = false;
  let historyError = '';
  let activeJobUploadSignature = '';
  let pollTimer = null;
  let showPasswordChange = false;
  let currentPasswordForChange = '';
  let newPassword = '';
  let confirmNewPassword = '';
  let passwordChangeMessage = '';
  let passwordChangeError = '';
  let isChangingPassword = false;
  let previewImage = null;
  let previewScale = 1;
  let previewPanX = 0;
  let previewPanY = 0;
  let isPreviewDragging = false;

  $: hasReadyUploads =
    uploads.length > 0 &&
    uploads.length <= maxUploads &&
    uploads.every((item) => (item.subjectName || '').trim().length > 0);

  $: canStart = Boolean(
    authorized &&
    selectedStyle &&
    hasReadyUploads &&
    !isSubmitting &&
    !isPolling
  );

  $: progressPercent = progress.total
    ? Math.round(((progress.completed + progress.failed) / progress.total) * 100)
    : 0;

  $: currentResultItems = sortResultItems(progress.items || []).map((item) => ({
    ...item,
    displayKey: `${item.batch_id || jobId || 'active'}-${item.id || item.item_index}`
  }));

  $: displayedResultItems = [
    ...resultHistory.flatMap((batch) => batch.items.map((item) => ({
      ...item,
      displayKey: `${batch.batchId}-${item.id || item.item_index}`
    }))),
    ...currentResultItems
  ];

  $: historyDateGroups = groupHistoryByDate(historyBatches);

  function emptyProgress(total = 0) {
    return { total, completed: 0, failed: 0, processing: total, done: false, items: [] };
  }

  function sortResultItems(items) {
    return (items || []).slice().sort((a, b) => a.item_index - b.item_index);
  }

  function apiUrl(path) {
    return `${API_BASE_URL}${path}`;
  }

  function authHeaders(extra = {}) {
    return {
      ...extra,
      'X-Cocker-Access-Token': accessToken
    };
  }

  async function verifyPassword() {
    accessError = '';
    const value = password.trim();
    if (!value) {
      accessError = '비밀번호를 입력해주세요.';
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/cocker/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: value })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || '비밀번호 확인에 실패했습니다.');
      }
      accessToken = data.access_token;
      authorized = true;
      password = '';
      await loadHistory(true);
    } catch (error) {
      accessError = error.message || '비밀번호 확인에 실패했습니다.';
    }
  }

  async function loadHistory(expandLatest = false) {
    if (!accessToken) return;
    historyError = '';
    isLoadingHistory = true;
    try {
      const response = await fetch(apiUrl('/api/cocker/history'), {
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || '이전 작업을 불러오지 못했습니다.');
      }
      historyBatches = data.batches || [];
      if (expandLatest && historyBatches.length > 0) {
        const latestDateKey = historyDayKey(historyBatches[0].created_at);
        expandedHistoryDateKeys = [latestDateKey];
        expandedHistoryIds = [historyBatches[0].batch_id];
      }
    } catch (error) {
      historyError = error.message || '이전 작업을 불러오지 못했습니다.';
    } finally {
      isLoadingHistory = false;
    }
  }

  function selectWorkspaceTab(tab) {
    activeWorkspaceTab = tab;
    if (tab === 'history' && historyBatches.length === 0 && !isLoadingHistory) {
      loadHistory(true);
    }
  }

  function toggleHistoryBatch(batchId) {
    expandedHistoryIds = expandedHistoryIds.includes(batchId)
      ? expandedHistoryIds.filter((id) => id !== batchId)
      : [...expandedHistoryIds, batchId];
  }

  function toggleHistoryDate(dateKey) {
    expandedHistoryDateKeys = expandedHistoryDateKeys.includes(dateKey)
      ? expandedHistoryDateKeys.filter((key) => key !== dateKey)
      : [...expandedHistoryDateKeys, dateKey];
  }

  function parseHistoryDate(value) {
    if (!value) return new Date(NaN);
    const text = String(value);
    const normalized = /(?:Z|[+-]\d{2}:\d{2})$/.test(text) ? text : `${text}Z`;
    return new Date(normalized);
  }

  function historyDayKey(value) {
    const parsed = parseHistoryDate(value);
    if (Number.isNaN(parsed.getTime())) return 'unknown';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(parsed);
    const part = (type) => parts.find((item) => item.type === type)?.value || '';
    return `${part('year')}-${part('month')}-${part('day')}`;
  }

  function historyDayLabel(value) {
    const parsed = parseHistoryDate(value);
    if (Number.isNaN(parsed.getTime())) return '날짜 정보 없음';
    return new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    }).format(parsed);
  }

  function groupHistoryByDate(batches) {
    const groups = new Map();
    for (const batch of batches || []) {
      const dateKey = historyDayKey(batch.created_at);
      if (!groups.has(dateKey)) {
        groups.set(dateKey, {
          dateKey,
          label: historyDayLabel(batch.created_at),
          batches: []
        });
      }
      groups.get(dateKey).batches.push(batch);
    }
    return Array.from(groups.values());
  }

  function historyDate(value) {
    const parsed = parseHistoryDate(value);
    if (Number.isNaN(parsed.getTime())) return '날짜 정보 없음';
    return new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed);
  }

  function historyStatus(batch) {
    if (!batch.done) return `진행 중 ${batch.completed + batch.failed}/${batch.total}`;
    if (batch.failed > 0) return `완료 ${batch.completed} · 실패 ${batch.failed}`;
    return `완료 ${batch.completed}`;
  }

  function openPasswordChange() {
    showPasswordChange = true;
    currentPasswordForChange = '';
    newPassword = '';
    confirmNewPassword = '';
    passwordChangeMessage = '';
    passwordChangeError = '';
  }

  function closePasswordChange() {
    if (isChangingPassword) return;
    showPasswordChange = false;
    currentPasswordForChange = '';
    newPassword = '';
    confirmNewPassword = '';
    passwordChangeError = '';
  }

  async function changePassword() {
    passwordChangeMessage = '';
    passwordChangeError = '';
    const currentValue = currentPasswordForChange;
    const value = newPassword.trim();
    const confirmValue = confirmNewPassword.trim();
    if (!currentValue) {
      passwordChangeError = '현재 비밀번호를 입력해주세요.';
      return;
    }
    if (value.length < 4 || value.length > 64) {
      passwordChangeError = '새 비밀번호는 4~64자로 입력해주세요.';
      return;
    }
    if (value !== confirmValue) {
      passwordChangeError = '새 비밀번호 확인이 일치하지 않습니다.';
      return;
    }

    try {
      isChangingPassword = true;
      const response = await fetch(apiUrl('/api/cocker/password'), {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          current_password: currentValue,
          new_password: value
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || '비밀번호 변경에 실패했습니다.');
      }
      passwordChangeMessage = '비밀번호가 변경되었습니다.';
      currentPasswordForChange = '';
      newPassword = '';
      confirmNewPassword = '';
    } catch (error) {
      passwordChangeError = error.message || '비밀번호 변경에 실패했습니다.';
    } finally {
      isChangingPassword = false;
    }
  }

  function clearUploads() {
    uploads.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    uploads = [];
    resetGeneratedResults();
    if (fileInput) {
      fileInput.value = '';
    }
  }

  function subjectNameFromFile(file) {
    const stem = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
    return stem.slice(0, 20) || '이름';
  }

  function fileKey(file) {
    return `${file.name}:${file.size}:${file.lastModified}`;
  }

  function isSupportedImage(file) {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return allowedUploadExtensions.has(extension);
  }

  function uploadSignature(list = uploads) {
    return list.map((item) => item.fileKey).join('|');
  }

  function resetGeneratedResults() {
    stopPolling();
    resultHistory = [];
    progress = emptyProgress();
    jobId = '';
    activeJobUploadSignature = '';
  }

  function archiveCompletedProgress(nextSignature) {
    if (!jobId || !progress.done || !(progress.items || []).length) {
      if (activeJobUploadSignature && activeJobUploadSignature !== nextSignature) {
        resultHistory = [];
      }
      return;
    }

    if (activeJobUploadSignature !== nextSignature) {
      resultHistory = [];
      return;
    }

    const exists = resultHistory.some((batch) => batch.batchId === jobId);
    if (!exists) {
      resultHistory = [
        ...resultHistory,
        { batchId: jobId, items: sortResultItems(progress.items) }
      ];
    }
  }

  function handleFiles(event) {
    submitError = '';
    const files = Array.from(event.target.files || []);
    const previousSignature = uploadSignature();
    const selectedKeys = new Set(uploads.map((item) => item.fileKey));
    const nextUploads = [...uploads];
    let duplicateCount = 0;
    let overflowCount = 0;
    let oversizedCount = 0;
    let unsupportedCount = 0;

    for (const file of files) {
      if (!isSupportedImage(file)) {
        unsupportedCount += 1;
        continue;
      }
      if (file.size > maxUploadBytes) {
        oversizedCount += 1;
        continue;
      }
      const key = fileKey(file);
      if (selectedKeys.has(key)) {
        duplicateCount += 1;
        continue;
      }

      if (nextUploads.length >= maxUploads) {
        overflowCount += 1;
        continue;
      }

      selectedKeys.add(key);
      nextUploads.push({
        id: `${Date.now()}-${nextUploads.length}-${file.name}-${file.size}`,
        file,
        fileKey: key,
        previewUrl: URL.createObjectURL(file),
        subjectName: subjectNameFromFile(file),
        breed: '',
        gender: ''
      });
    }

    uploads = nextUploads;
    if (uploadSignature(nextUploads) !== previousSignature) {
      resetGeneratedResults();
    }
    if (event.currentTarget) {
      event.currentTarget.value = '';
    }

    const fileErrors = [];
    if (unsupportedCount > 0) fileErrors.push('PNG, JPG, WEBP, GIF 파일만 선택할 수 있습니다.');
    if (oversizedCount > 0) fileErrors.push('이미지 한 장의 크기는 10MB 이하여야 합니다.');
    if (overflowCount > 0) fileErrors.push(`이미지는 최대 ${maxUploads}장까지 업로드할 수 있습니다.`);
    if (duplicateCount > 0) fileErrors.push('이미 선택한 파일은 추가하지 않았습니다.');
    submitError = fileErrors.join(' ');
  }

  function updateSubject(id, value) {
    uploads = uploads.map((item) => item.id === id ? { ...item, subjectName: value } : item);
  }

  function updateBreed(id, value) {
    uploads = uploads.map((item) => item.id === id ? { ...item, breed: value } : item);
  }

  function updateGender(id, value) {
    uploads = uploads.map((item) => item.id === id ? { ...item, gender: value } : item);
  }

  function removeUpload(id) {
    const target = uploads.find((item) => item.id === id);
    if (target) {
      URL.revokeObjectURL(target.previewUrl);
    }
    uploads = uploads.filter((item) => item.id !== id);
    resetGeneratedResults();
  }

  function originalUrlForItem(item) {
    const local = uploads[item.item_index];
    return item.original_url || local?.previewUrl || '';
  }

  function petMeta(item) {
    return [item.breed, item.gender].filter(Boolean).join(' · ');
  }

  async function startJob() {
    if (!canStart) return;

    submitError = '';
    const nextSignature = uploadSignature();
    archiveCompletedProgress(nextSignature);
    isSubmitting = true;
    activeJobUploadSignature = nextSignature;
    progress = emptyProgress(uploads.length);

    const formData = new FormData();
    formData.append('style', selectedStyle);
    formData.append('access_token', accessToken);
    formData.append('subject_names', JSON.stringify(uploads.map((item) => item.subjectName.trim())));
    formData.append('breeds', JSON.stringify(uploads.map((item) => (item.breed || '').trim())));
    formData.append('genders', JSON.stringify(uploads.map((item) => (item.gender || '').trim())));
    uploads.forEach((item) => {
      formData.append('images', item.file, item.file.name);
    });

    try {
      const response = await fetch(apiUrl('/api/cocker/jobs'), {
        method: 'POST',
        headers: authHeaders(),
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || '작업 생성에 실패했습니다.');
      }
      jobId = data.batch_id;
      activeJobUploadSignature = nextSignature;
      progress = {
        total: data.total || uploads.length,
        completed: 0,
        failed: 0,
        processing: data.total || uploads.length,
        done: false,
        items: data.items || []
      };
      beginPolling();
    } catch (error) {
      submitError = error.message || '작업 생성에 실패했습니다.';
    } finally {
      isSubmitting = false;
    }
  }

  function beginPolling() {
    stopPolling();
    isPolling = true;
    pollJob();
    pollTimer = setInterval(pollJob, 2500);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    isPolling = false;
  }

  async function pollJob() {
    if (!jobId) return;

    try {
      const response = await fetch(apiUrl(`/api/cocker/jobs/${jobId}`), {
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || '작업 상태 확인에 실패했습니다.');
      }
      progress = data;
      if (data.done) {
        stopPolling();
        loadHistory();
      }
    } catch (error) {
      submitError = error.message || '작업 상태 확인에 실패했습니다.';
      stopPolling();
    }
  }

  function downloadAll() {
    if (!jobId || !progress.done || progress.completed !== progress.total) return;
    const url = apiUrl(`/api/cocker/jobs/${jobId}/download?access_token=${encodeURIComponent(accessToken)}`);
    downloadFile(url, `cocker_${timestampForFile()}_${jobId}.zip`);
  }

  function historyDateCompletedCount(dateGroup) {
    return (dateGroup?.batches || []).reduce((total, batch) => total + batch.completed, 0);
  }

  function downloadHistoryDate(dateGroup) {
    if (!dateGroup?.dateKey || historyDateCompletedCount(dateGroup) < 1) return;
    const url = apiUrl(`/api/cocker/history/${encodeURIComponent(dateGroup.dateKey)}/download?access_token=${encodeURIComponent(accessToken)}`);
    downloadFile(url, `cocker_${dateGroup.dateKey.replaceAll('-', '')}.zip`);
  }

  function downloadImage(url, name) {
    if (!url) return;
    downloadFile(url, name);
  }

  function timestampForFile(value = null) {
    const parsed = value ? new Date(value) : new Date();
    const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    const pad = (number) => String(number).padStart(2, '0');
    return [
      date.getFullYear(),
      pad(date.getMonth() + 1),
      pad(date.getDate())
    ].join('') + '_' + [
      pad(date.getHours()),
      pad(date.getMinutes()),
      pad(date.getSeconds())
    ].join('');
  }

  function imageFileName(item, slot) {
    const subject = (item.subject_name || 'image').replace(/[\\/:*?"<>|]/g, '_');
    return `${String(item.item_index + 1).padStart(3, '0')}_${subject}_${slot}_${timestampForFile(item.completed_at)}.png`;
  }

  function openImagePreview(url, alt) {
    if (!url) return;
    previewImage = { url, alt };
    previewScale = 1;
    previewPanX = 0;
    previewPanY = 0;
  }

  function closeImagePreview() {
    previewImage = null;
    previewScale = 1;
    previewPanX = 0;
    previewPanY = 0;
    isPreviewDragging = false;
  }

  function handlePreviewKeydown(event) {
    if (event.key === 'Escape' && previewImage) {
      closeImagePreview();
    }
  }

  function handlePreviewBackdropClick(event) {
    const target = event.target;
    if (target instanceof Element && target.closest('.image-preview-modal img')) return;
    closeImagePreview();
  }

  function handlePreviewWheel(event) {
    if (!previewImage) return;
    const zoomFactor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    const nextScale = Math.min(3, Math.max(0.5, previewScale * zoomFactor));
    const image = event.currentTarget.querySelector('.image-preview-modal img');
    if (image) {
      const nextPan = clampPreviewPan(image, previewPanX, previewPanY, nextScale);
      previewPanX = nextPan.x;
      previewPanY = nextPan.y;
    }
    previewScale = nextScale;
  }

  function clampPreviewPan(image, x, y, scale = previewScale) {
    const stage = image.closest('.image-preview-modal');
    if (!stage) return { x, y };
    const maxX = Math.max(0, (image.offsetWidth * scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * scale - stage.clientHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y))
    };
  }

  function draggablePreviewImage(node) {
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startPanX = 0;
    let startPanY = 0;

    const handlePointerDown = (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startPanX = previewPanX;
      startPanY = previewPanY;
      isPreviewDragging = true;
      node.setPointerCapture(pointerId);
    };

    const handlePointerMove = (event) => {
      if (event.pointerId !== pointerId || !isPreviewDragging) return;
      const nextPan = clampPreviewPan(
        node,
        startPanX + event.clientX - startX,
        startPanY + event.clientY - startY
      );
      previewPanX = nextPan.x;
      previewPanY = nextPan.y;
    };

    const stopDragging = (event) => {
      if (event.pointerId !== pointerId) return;
      if (node.hasPointerCapture(pointerId)) node.releasePointerCapture(pointerId);
      pointerId = null;
      isPreviewDragging = false;
    };

    node.addEventListener('pointerdown', handlePointerDown);
    node.addEventListener('pointermove', handlePointerMove);
    node.addEventListener('pointerup', stopDragging);
    node.addEventListener('pointercancel', stopDragging);
    return {
      destroy() {
        node.removeEventListener('pointerdown', handlePointerDown);
        node.removeEventListener('pointermove', handlePointerMove);
        node.removeEventListener('pointerup', stopDragging);
        node.removeEventListener('pointercancel', stopDragging);
        isPreviewDragging = false;
      }
    };
  }

  function imagePreviewOverlay(node) {
    const handleClick = (event) => handlePreviewBackdropClick(event);
    const handleWheel = (event) => {
      event.preventDefault();
      handlePreviewWheel(event);
    };
    node.addEventListener('click', handleClick);
    node.addEventListener('wheel', handleWheel, { passive: false });
    return {
      destroy() {
        node.removeEventListener('click', handleClick);
        node.removeEventListener('wheel', handleWheel);
      }
    };
  }

  onDestroy(() => {
    stopPolling();
    uploads.forEach((item) => URL.revokeObjectURL(item.previewUrl));
  });
</script>

<svelte:window on:keydown={handlePreviewKeydown} />
<svelte:body class:image-preview-open={Boolean(previewImage)} />

{#if !authorized}
  <div class="gate-page">
    <form class="gate-panel" on:submit|preventDefault={verifyPassword}>
      <img src="/images/e_rion_logo.png" alt="E-RION" class="gate-logo" />
      <h1>접속 비밀번호</h1>
      <input
        type="password"
        bind:value={password}
        placeholder="비밀번호"
        autocomplete="current-password"
        class="gate-input"
      />
      {#if accessError}
        <p class="error-text">{accessError}</p>
      {/if}
      <button class="primary-btn" type="submit">접속</button>
    </form>
  </div>
{:else}
  <section class="cocker-page">
    <header class="page-top">
      <div>
        <img src="/images/이리온 아이콘.png" alt="E-RION" class="page-logo" />
        <h1>이리온 스튜디오</h1>
      </div>
      <button class="secondary-btn" type="button" on:click={openPasswordChange}>
        비밀번호 변경
      </button>
    </header>

    {#if previewImage}
      <div
        class="image-preview-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label="이미지 크게 보기"
        use:imagePreviewOverlay
      >
        <div class="image-preview-modal">
          <button
            class="image-preview-close"
            type="button"
            on:click|stopPropagation={closeImagePreview}
            aria-label="이미지 프리뷰 닫기"
            title="닫기"
          >
            ×
          </button>
          <img
            src={previewImage.url}
            alt={previewImage.alt}
            draggable="false"
            class:dragging={isPreviewDragging}
            use:draggablePreviewImage
            style={`transform: translate3d(${previewPanX}px, ${previewPanY}px, 0) scale(${previewScale})`}
          />
        </div>
      </div>
    {/if}

    {#if showPasswordChange}
      <div class="modal-backdrop">
        <form class="password-modal" on:submit|preventDefault={changePassword}>
          <div class="modal-heading">
            <h2>비밀번호 변경</h2>
            <button
              class="icon-btn modal-close"
              type="button"
              on:click={closePasswordChange}
              aria-label="닫기"
              title="닫기"
              disabled={isChangingPassword}
            >
              ×
            </button>
          </div>

          <label class="modal-field" for="current-cocker-password">
            <span>현재 비밀번호</span>
            <input
              id="current-cocker-password"
              type="password"
              bind:value={currentPasswordForChange}
              autocomplete="current-password"
            />
          </label>

          <label class="modal-field" for="new-cocker-password">
            <span>새로운 비밀번호</span>
            <input
              id="new-cocker-password"
              type="password"
              bind:value={newPassword}
              autocomplete="new-password"
            />
          </label>

          <label class="modal-field" for="confirm-cocker-password">
            <span>새로운 비밀번호 확인</span>
            <input
              id="confirm-cocker-password"
              type="password"
              bind:value={confirmNewPassword}
              autocomplete="new-password"
            />
          </label>

          {#if passwordChangeMessage}<p class="success-text">{passwordChangeMessage}</p>{/if}
          {#if passwordChangeError}<p class="error-text">{passwordChangeError}</p>{/if}

          <div class="modal-actions">
            <button class="secondary-btn" type="button" on:click={closePasswordChange} disabled={isChangingPassword}>
              취소
            </button>
            <button class="primary-btn" type="submit" disabled={isChangingPassword}>
              {isChangingPassword ? '변경 중' : '변경하기'}
            </button>
          </div>
        </form>
      </div>
    {/if}

    <div class="workspace-tabs" role="tablist" aria-label="스튜디오 작업 메뉴">
      <button
        id="cocker-create-tab"
        type="button"
        role="tab"
        class:active={activeWorkspaceTab === 'create'}
        aria-selected={activeWorkspaceTab === 'create'}
        aria-controls="cocker-workspace-panel"
        on:click={() => selectWorkspaceTab('create')}
      >
        새 작업
      </button>
      <button
        id="cocker-history-tab"
        type="button"
        role="tab"
        class:active={activeWorkspaceTab === 'history'}
        aria-selected={activeWorkspaceTab === 'history'}
        aria-controls="cocker-workspace-panel"
        on:click={() => selectWorkspaceTab('history')}
      >
        이전 작업
      </button>
    </div>

    <div
      id="cocker-workspace-panel"
      class="workflow"
      role="tabpanel"
      aria-labelledby={activeWorkspaceTab === 'create' ? 'cocker-create-tab' : 'cocker-history-tab'}
    >
      {#if activeWorkspaceTab === 'create'}
      <section class="section-band">
        <div class="section-heading">
          <span>1</span>
          <h2>스타일 선택</h2>
        </div>
        <div class="style-grid">
          {#each styleOptions as style}
            <button
              type="button"
              class="style-card"
              class:selected={selectedStyle === style.id}
              on:click={() => selectedStyle = style.id}
              aria-pressed={selectedStyle === style.id}
            >
              <strong>{style.label}</strong>
              <div class="example-grid">
                {#each style.examples as image}
                  <span class="example-tile">
                    <span>샘플 준비 중</span>
                    <img
                      src={image}
                      alt={style.label}
                      loading="lazy"
                      on:error={(event) => event.currentTarget.hidden = true}
                    />
                  </span>
                {/each}
              </div>
            </button>
          {/each}
        </div>
      </section>

      <section class="section-band">
        <div class="section-heading">
          <span>2</span>
          <h2>이미지와 정보</h2>
        </div>
        <label class="upload-zone">
          <input
            bind:this={fileInput}
            type="file"
            accept=".png,.jpg,.jpeg,.webp,.gif"
            multiple
            on:change={handleFiles}
            disabled={isSubmitting || isPolling}
          />
          <span>이미지 선택</span>
          <small>{uploads.length}/{maxUploads}</small>
        </label>

        {#if uploads.length > 0}
          <div class="name-list">
            {#each uploads as upload, index}
              <div class="name-row">
                <img class="upload-thumb" src={upload.previewUrl} alt="업로드 이미지 {index + 1}" />
                <div class="upload-fields">
                  <div class="field-group">
                    <label for={"subject-" + upload.id}>피사체 이름</label>
                    <input
                      id={"subject-" + upload.id}
                      value={upload.subjectName}
                      on:input={(event) => updateSubject(upload.id, event.currentTarget.value)}
                      disabled={isSubmitting || isPolling}
                    />
                  </div>
                  <div class="pet-meta-grid">
                    <div class="field-group">
                      <label for={"breed-" + upload.id}>품종 <small>선택</small></label>
                      <input
                        id={"breed-" + upload.id}
                        value={upload.breed}
                        placeholder="예: 코카스파니엘"
                        on:input={(event) => updateBreed(upload.id, event.currentTarget.value)}
                        disabled={isSubmitting || isPolling}
                      />
                    </div>
                    <div class="field-group">
                      <label for={"gender-" + upload.id}>성별 <small>선택</small></label>
                      <select
                        id={"gender-" + upload.id}
                        value={upload.gender}
                        on:change={(event) => updateGender(upload.id, event.currentTarget.value)}
                        disabled={isSubmitting || isPolling}
                      >
                        {#each genderOptions as option}
                          <option value={option}>{option || '선택 안 함'}</option>
                        {/each}
                      </select>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="icon-btn"
                  on:click={() => removeUpload(upload.id)}
                  disabled={isSubmitting || isPolling}
                  aria-label="이미지 제거"
                  title="이미지 제거"
                >
                  ×
                </button>
              </div>
            {/each}
          </div>
        {/if}
      </section>

      <section class="control-band">
        <div class="progress-area">
          <div class="progress-label" aria-live="polite">
            {#if isSubmitting}
              <span class="uploading-spinner" aria-hidden="true"></span>
              <strong>이미지를 올리는 중</strong>
            {:else}
              <strong>{progress.completed}/{progress.total || uploads.length}</strong>
              <span>완료</span>
              {#if progress.failed > 0}
                <span class="failed-count">실패 {progress.failed}</span>
              {/if}
            {/if}
          </div>
          <div
            class="progress-track"
            class:uploading={isSubmitting}
            aria-label={isSubmitting ? '이미지 업로드 진행 중' : '작업 진행률'}
            aria-busy={isSubmitting}
          >
            <div
              class="progress-fill"
              style={isSubmitting ? '' : `width: ${progressPercent}%`}
            ></div>
          </div>
        </div>
        <button
          class="primary-btn"
          class:uploading={isSubmitting}
          type="button"
          on:click={startJob}
          disabled={!canStart}
        >
          {isSubmitting ? '이미지를 올리는 중' : '스타일 변환'}
        </button>
      </section>

      {#if submitError}
        <p class="error-text main-error">{submitError}</p>
      {/if}

      {#if progress.done && progress.completed === progress.total && progress.total > 0}
        <div class="download-all-row">
          <button class="primary-btn" type="button" on:click={downloadAll}>만들어진 모든 이미지 다운로드</button>
        </div>
      {/if}

      {#if displayedResultItems.length > 0}
        <section class="results-shell">
          <div class="result-header">
            <span>원본 이미지</span>
            <span>생성 이미지</span>
            <span>생성 이미지</span>
          </div>
          <div class="result-scroll">
            {#each displayedResultItems as item (item.displayKey)}
              <div class="result-row">
                <figure class="image-cell">
                  <button
                    class="image-preview-trigger"
                    type="button"
                    on:click={() => openImagePreview(originalUrlForItem(item), `${item.subject_name} 원본 이미지`)}
                    aria-label={`${item.subject_name} 원본 이미지 크게 보기`}
                  >
                    <img src={originalUrlForItem(item)} alt="원본 이미지" />
                  </button>
                  <figcaption class="original-caption">
                    <span>{item.subject_name}</span>
                    {#if petMeta(item)}
                      <small>{petMeta(item)}</small>
                    {/if}
                  </figcaption>
                </figure>

                <figure class="image-cell">
                  {#if item.result_url_1}
                    <button
                      class="image-preview-trigger"
                      type="button"
                      on:click={() => openImagePreview(item.result_url_1, `${item.subject_name} 생성 이미지 1`)}
                      aria-label={`${item.subject_name} 생성 이미지 1 크게 보기`}
                    >
                      <img src={item.result_url_1} alt="생성 이미지 1" />
                    </button>
                    <figcaption>
                      <span>결과 1</span>
                      <button type="button" on:click={() => downloadImage(item.result_url_1, imageFileName(item, 'generated_1'))}>
                        다운로드
                      </button>
                    </figcaption>
                  {:else}
                    <div class="placeholder-cell" class:failed={item.process_status === 'failed'}>
                      {#if item.process_status === 'failed'}
                        <span>실패</span>
                      {:else}
                        <span class="loading-spinner" aria-hidden="true"></span>
                        <span>생성 중</span>
                      {/if}
                    </div>
                  {/if}
                </figure>

                <figure class="image-cell">
                  {#if item.result_url_2}
                    <button
                      class="image-preview-trigger"
                      type="button"
                      on:click={() => openImagePreview(item.result_url_2, `${item.subject_name} 생성 이미지 2`)}
                      aria-label={`${item.subject_name} 생성 이미지 2 크게 보기`}
                    >
                      <img src={item.result_url_2} alt="생성 이미지 2" />
                    </button>
                    <figcaption>
                      <span>결과 2</span>
                      <button type="button" on:click={() => downloadImage(item.result_url_2, imageFileName(item, 'generated_2'))}>
                        다운로드
                      </button>
                    </figcaption>
                  {:else}
                    <div class="placeholder-cell" class:failed={item.process_status === 'failed'}>
                      {#if item.process_status === 'failed'}
                        <span>실패</span>
                      {:else}
                        <span class="loading-spinner" aria-hidden="true"></span>
                        <span>생성 중</span>
                      {/if}
                    </div>
                  {/if}
                </figure>
              </div>
            {/each}
          </div>
        </section>
      {/if}

      {:else}
      <section class="history-section">
        <div class="history-heading">
          <div>
            <h2>이전 작업</h2>
            <span>{historyBatches.length}건</span>
          </div>
          <button
            class="secondary-btn history-refresh"
            type="button"
            on:click={() => loadHistory()}
            disabled={isLoadingHistory}
          >
            {isLoadingHistory ? '불러오는 중' : '새로고침'}
          </button>
        </div>

        {#if historyError}
          <p class="error-text main-error">{historyError}</p>
        {:else if isLoadingHistory && historyBatches.length === 0}
          <p class="history-empty">이전 작업을 불러오는 중입니다.</p>
        {:else if historyBatches.length === 0}
          <p class="history-empty">아직 이전 작업이 없습니다.</p>
        {:else}
          <div class="history-date-list">
            {#each historyDateGroups as dateGroup (dateGroup.dateKey)}
              <section class="history-date-group">
                <div class="history-date-header">
                  <button
                    class="history-date-toggle"
                    type="button"
                    on:click={() => toggleHistoryDate(dateGroup.dateKey)}
                    aria-expanded={expandedHistoryDateKeys.includes(dateGroup.dateKey)}
                  >
                    <span class="history-date-label">
                      <strong>{dateGroup.label}</strong>
                      <small>{dateGroup.batches.length}건</small>
                    </span>
                    <span
                      class="history-date-state"
                      class:expanded={expandedHistoryDateKeys.includes(dateGroup.dateKey)}
                    >
                      <span aria-hidden="true">
                        {expandedHistoryDateKeys.includes(dateGroup.dateKey) ? '▲' : '▼'}
                      </span>
                      {expandedHistoryDateKeys.includes(dateGroup.dateKey) ? '접기' : '보기'}
                    </span>
                  </button>
                  {#if historyDateCompletedCount(dateGroup) > 0}
                    <button
                      class="secondary-btn history-date-download"
                      type="button"
                      on:click={() => downloadHistoryDate(dateGroup)}
                    >
                      날짜 전체 다운로드
                    </button>
                  {/if}
                </div>

                {#if expandedHistoryDateKeys.includes(dateGroup.dateKey)}
                  <div class="history-list">
                    {#each dateGroup.batches as batch (batch.batch_id)}
                      <article class="history-batch">
                        <button
                          class="history-summary"
                          type="button"
                          on:click={() => toggleHistoryBatch(batch.batch_id)}
                          aria-expanded={expandedHistoryIds.includes(batch.batch_id)}
                        >
                          <span class="history-meta">
                            <strong>{batch.style_name}</strong>
                            <span>{historyDate(batch.created_at)}</span>
                            <small>{batch.total}장 · {historyStatus(batch)}</small>
                          </span>
                          <span class="history-actions">
                            <span class="primary-btn history-result-toggle">
                              <span aria-hidden="true">
                                {expandedHistoryIds.includes(batch.batch_id) ? '▲' : '▼'}
                              </span>
                              {expandedHistoryIds.includes(batch.batch_id) ? '접기' : '결과 보기'}
                            </span>
                          </span>
                        </button>

                        {#if expandedHistoryIds.includes(batch.batch_id)}
                          <div class="history-results results-shell">
                            <div class="result-header">
                              <span>원본 이미지</span>
                              <span>생성 이미지</span>
                              <span>생성 이미지</span>
                            </div>
                            <div class="result-scroll">
                              {#each batch.items as item (item.id)}
                                <div class="result-row">
                                  <figure class="image-cell">
                                    <button
                                      class="image-preview-trigger"
                                      type="button"
                                      on:click={() => openImagePreview(item.original_url, `${item.subject_name} 원본 이미지`)}
                                      aria-label={`${item.subject_name} 원본 이미지 크게 보기`}
                                    >
                                      <img src={item.original_url} alt="원본 이미지" />
                                    </button>
                                    <figcaption class="original-caption">
                                      <span>{item.subject_name}</span>
                                      {#if petMeta(item)}
                                        <small>{petMeta(item)}</small>
                                      {/if}
                                    </figcaption>
                                  </figure>

                                  <figure class="image-cell">
                                    {#if item.result_url_1}
                                      <button
                                        class="image-preview-trigger"
                                        type="button"
                                        on:click={() => openImagePreview(item.result_url_1, `${item.subject_name} 생성 이미지 1`)}
                                        aria-label={`${item.subject_name} 생성 이미지 1 크게 보기`}
                                      >
                                        <img src={item.result_url_1} alt="생성 이미지 1" />
                                      </button>
                                      <figcaption>
                                        <span>결과 1</span>
                                        <button type="button" on:click={() => downloadImage(item.result_url_1, imageFileName(item, 'generated_1'))}>
                                          다운로드
                                        </button>
                                      </figcaption>
                                    {:else}
                                      <div class="placeholder-cell" class:failed={item.process_status === 'failed'}>
                                        <span>{item.process_status === 'failed' ? '실패' : '생성 중'}</span>
                                      </div>
                                    {/if}
                                  </figure>

                                  <figure class="image-cell">
                                    {#if item.result_url_2}
                                      <button
                                        class="image-preview-trigger"
                                        type="button"
                                        on:click={() => openImagePreview(item.result_url_2, `${item.subject_name} 생성 이미지 2`)}
                                        aria-label={`${item.subject_name} 생성 이미지 2 크게 보기`}
                                      >
                                        <img src={item.result_url_2} alt="생성 이미지 2" />
                                      </button>
                                      <figcaption>
                                        <span>결과 2</span>
                                        <button type="button" on:click={() => downloadImage(item.result_url_2, imageFileName(item, 'generated_2'))}>
                                          다운로드
                                        </button>
                                      </figcaption>
                                    {:else}
                                      <div class="placeholder-cell" class:failed={item.process_status === 'failed'}>
                                        <span>{item.process_status === 'failed' ? '실패' : '생성 중'}</span>
                                      </div>
                                    {/if}
                                  </figure>
                                </div>
                              {/each}
                            </div>
                          </div>
                        {/if}
                      </article>
                    {/each}
                  </div>
                {/if}
              </section>
            {/each}
          </div>
        {/if}
      </section>
      {/if}
    </div>
  </section>
{/if}

<style>
  :global(body.light) {
    background-color: #f5f5f5;
  }

  .gate-page {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 2rem;
    background:
      linear-gradient(180deg, rgba(245, 245, 245, 0.94), rgba(245, 245, 245, 0.98)),
      url('/images/hero/hero-dog.png') center/cover;
  }

  .gate-panel {
    width: min(380px, 100%);
    padding: 2rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    display: grid;
    gap: 1rem;
  }

  .gate-logo {
    width: 112px;
    height: auto;
  }

  .gate-panel h1,
  .page-top h1,
  .section-heading h2 {
    margin: 0;
    letter-spacing: 0;
  }

  .gate-panel h1 {
    font-size: 1.55rem;
    color: #404040;
  }

  .gate-input,
  .password-modal input,
  .name-row input,
  .name-row select {
    width: 100%;
    min-height: 44px;
    border: 1px solid rgba(0, 0, 0, 0.14);
    border-radius: 6px;
    padding: 0.75rem 0.85rem;
    background: #ffffff;
    color: #404040;
    font: inherit;
  }

  .gate-input:focus,
  .password-modal input:focus,
  .name-row input:focus,
  .name-row select:focus {
    border-color: #FE6A37;
    box-shadow: 0 0 0 3px rgba(254, 106, 55, 0.12);
    outline: none;
  }

  .cocker-page {
    width: min(1200px, 100%);
    margin: 0 auto;
    padding: 2rem 1rem;
    color: #404040;
  }

  .page-top {
    min-height: 78px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .page-top > div {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }

  .page-logo {
    width: 65px;
    height: auto;
  }

  .page-top h1 {
    font-size: clamp(1.35rem, 3vw, 2rem);
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .workspace-tabs {
    display: flex;
    gap: 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .workspace-tabs button {
    min-width: 88px;
    min-height: 50px;
    padding: 0.8rem 0.15rem 0.65rem;
    border: 0;
    border-bottom: 3px solid transparent;
    background: transparent;
    color: #777777;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
  }

  .workspace-tabs button:hover,
  .workspace-tabs button.active {
    border-bottom-color: #FE6A37;
    color: #FE6A37;
  }

  .workspace-tabs button:focus-visible {
    outline: 2px solid #FE6A37;
    outline-offset: 3px;
  }

  .workflow {
    display: grid;
    gap: 1.25rem;
    padding: 1.25rem 0 2rem;
  }

  .section-band,
  .control-band {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 1.25rem;
  }

  .workflow > .section-band:first-child {
    border-top: none;
  }

  .section-heading {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.9rem;
  }

  .section-heading span {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    color: #fff;
    font-weight: 800;
  }

  .section-heading h2 {
    font-size: 1.1rem;
  }

  .style-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    align-items: start;
    gap: 1rem;
  }

  .style-card {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.7rem;
    padding: 0.75rem;
    border: 2px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    background: #ffffff;
    color: #555555;
    cursor: pointer;
    box-shadow: 0 3px 14px rgba(0, 0, 0, 0.06);
    transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  }

  .style-card:hover {
    border-color: rgba(254, 106, 55, 0.55);
    color: #FE6A37;
    box-shadow: 0 7px 22px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .style-card:focus-visible {
    outline: 3px solid rgba(254, 106, 55, 0.28);
    outline-offset: 3px;
  }

  .style-card.selected {
    border-color: #FE6A37;
    background: #ffffff;
    color: #FE6A37;
    box-shadow: 0 8px 26px rgba(254, 106, 55, 0.2);
  }

  .example-grid {
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.55rem;
    min-width: 0;
  }

  .example-tile {
    position: relative;
    aspect-ratio: 1;
    min-width: 0;
    min-height: 0;
    display: grid;
    place-items: center;
    overflow: hidden;
    border-radius: 6px;
    background: #f2f2f2;
    color: #888888;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .example-tile > img {
    position: absolute;
    inset: 0;
    object-position: center;
  }

  .example-tile > img,
  .upload-thumb {
    width: 100%;
    height: 100%;
    min-width: 0;
    object-fit: cover;
    display: block;
  }

  .style-card strong {
    display: block;
    padding: 0.25rem 0.15rem 0.4rem;
    font-size: 1.02rem;
    line-height: 1.35;
    letter-spacing: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .upload-zone {
    min-height: 112px;
    border: 1.5px dashed rgba(254, 106, 55, 0.42);
    border-radius: 8px;
    display: grid;
    place-items: center;
    gap: 0.25rem;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.88), rgba(255, 248, 245, 0.88));
    cursor: pointer;
    transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  }

  .upload-zone:hover {
    border-color: #FE6A37;
    background: rgba(254, 106, 55, 0.05);
    box-shadow: 0 8px 24px rgba(254, 106, 55, 0.12);
  }

  .upload-zone input {
    display: none;
  }

  .upload-zone span {
    font-weight: 800;
    color: #404040;
  }

  .upload-zone small {
    color: #666666;
  }

  .name-list {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(390px, 1fr));
    gap: 0.75rem;
  }

  .name-row {
    display: grid;
    grid-template-columns: 86px minmax(0, 1fr) 34px;
    align-items: start;
    gap: 0.65rem;
    padding: 0.75rem;
    border: 1px solid rgba(254, 106, 55, 0.16);
    border-radius: 8px;
    background:
      linear-gradient(180deg, #ffffff, #fff8f5);
    box-shadow: 0 8px 22px rgba(254, 106, 55, 0.08);
    min-width: 0;
    overflow: hidden;
  }

  .upload-thumb {
    width: 86px;
    height: 86px;
    border-radius: 6px;
    background: #f2f2f2;
  }

  .upload-fields {
    display: grid;
    gap: 0.6rem;
    min-width: 0;
  }

  .field-group {
    min-width: 0;
  }

  .pet-meta-grid {
    display: grid;
    grid-template-columns: minmax(190px, 1.6fr) minmax(120px, 0.8fr);
    gap: 0.55rem;
  }

  .name-row label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.82rem;
    color: #666666;
  }

  .name-row label small {
    color: #FE6A37;
    font-weight: 800;
  }

  .name-row select {
    appearance: none;
    background-image:
      linear-gradient(45deg, transparent 50%, #FE6A37 50%),
      linear-gradient(135deg, #FE6A37 50%, transparent 50%);
    background-position:
      calc(100% - 15px) 18px,
      calc(100% - 10px) 18px;
    background-size: 5px 5px, 5px 5px;
    background-repeat: no-repeat;
    padding-right: 2rem;
  }

  .icon-btn {
    width: 34px;
    height: 34px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 50%;
    background: #fff;
    color: #555555;
    cursor: pointer;
    font-size: 1.2rem;
    line-height: 1;
  }

  .control-band {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 1rem;
  }

  .progress-area {
    display: grid;
    gap: 0.55rem;
  }

  .progress-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #666666;
  }

  .progress-label strong {
    color: #404040;
    font-size: 1.15rem;
  }

  .uploading-spinner {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    border: 2px solid rgba(254, 106, 55, 0.25);
    border-top-color: #FE6A37;
    border-radius: 50%;
    animation: cockerSpin 0.8s linear infinite;
  }

  .failed-count {
    color: #d94747;
  }

  .progress-track {
    height: 10px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    transition: width 0.35s ease;
  }

  .progress-track.uploading .progress-fill {
    width: 35%;
    animation: cockerUploadProgress 1.1s ease-in-out infinite;
  }

  @keyframes cockerUploadProgress {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(286%);
    }
  }

  .control-band > .primary-btn {
    min-width: 160px;
  }

  .primary-btn,
  .secondary-btn,
  .image-cell button {
    border: none;
    border-radius: 6px;
    min-height: 42px;
    padding: 0.72rem 1rem;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
  }

  .primary-btn {
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    color: #fff;
    box-shadow: 0 4px 15px rgba(254, 106, 55, 0.3);
    transition: box-shadow 0.25s ease, transform 0.25s ease, opacity 0.25s ease;
  }

  .primary-btn:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(254, 106, 55, 0.5);
    transform: translateY(-1px);
  }

  .primary-btn:disabled {
    background: #bdbdbd;
    box-shadow: none;
    cursor: not-allowed;
  }

  .control-band > .primary-btn.uploading:disabled {
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    color: #ffffff;
    opacity: 0.82;
  }

  .secondary-btn,
  .image-cell button {
    background: transparent;
    border: 1px solid #FE6A37;
    color: #FE6A37;
    transition: background 0.25s ease, color 0.25s ease, opacity 0.25s ease;
  }

  .secondary-btn:hover,
  .image-cell button:hover {
    background: #FE6A37;
    color: #fff;
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: grid;
    place-items: center;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.42);
  }

  .image-preview-backdrop {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: grid;
    place-items: center;
    box-sizing: border-box;
    padding: 4.5rem 2rem 2rem;
    overflow: hidden;
    overscroll-behavior: contain;
    background: rgba(0, 0, 0, 0.88);
  }

  .image-preview-modal {
    width: min(1100px, calc(100vw - 6rem));
    height: min(820px, 80vh);
    height: min(820px, 80dvh);
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);
    place-items: center;
    overflow: visible;
  }

  .image-preview-modal img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    min-width: 0;
    min-height: 0;
    object-fit: contain;
    display: block;
    transform-origin: center;
    transition: transform 0.08s ease-out;
    will-change: transform;
    cursor: grab;
    user-select: none;
    touch-action: none;
  }

  .image-preview-modal img.dragging {
    cursor: grabbing;
    transition: none;
  }

  :global(body.image-preview-open) {
    overflow: hidden;
  }

  .image-preview-close {
    position: fixed;
    top: 1rem;
    right: 1rem;
    z-index: 101;
    width: 44px;
    height: 44px;
    padding: 0;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.7);
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.45);
    color: #ffffff;
    font-size: 1.7rem;
    line-height: 1;
  }

  .image-preview-close:hover {
    background: #FE6A37;
    border-color: #FE6A37;
  }

  .password-modal {
    width: min(430px, 100%);
    display: grid;
    gap: 0.95rem;
    padding: 1.35rem;
    border: 1px solid rgba(254, 106, 55, 0.18);
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.22);
  }

  .modal-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .modal-heading h2 {
    margin: 0;
    color: #404040;
    font-size: 1.2rem;
    letter-spacing: 0;
  }

  .modal-close {
    flex: 0 0 auto;
  }

  .modal-field {
    display: grid;
    gap: 0.35rem;
  }

  .modal-field span {
    color: #666666;
    font-size: 0.88rem;
    font-weight: 800;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
    margin-top: 0.2rem;
  }

  .error-text,
  .success-text {
    margin: 0;
    font-size: 0.92rem;
  }

  .error-text {
    color: #d94747;
  }

  .success-text {
    color: #FE6A37;
  }

  .main-error {
    padding: 0.8rem 1rem;
    background: rgba(217, 71, 71, 0.08);
    border-radius: 6px;
  }

  .download-all-row {
    display: flex;
    justify-content: flex-end;
  }

  .history-section {
    display: grid;
    gap: 1rem;
  }

  .history-heading,
  .history-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .history-summary {
    width: 100%;
    padding: 0.2rem 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .history-summary:hover .history-meta strong {
    color: #FE6A37;
  }

  .history-summary:focus-visible {
    outline: 2px solid #FE6A37;
    outline-offset: 4px;
  }

  .history-heading > div {
    display: flex;
    align-items: baseline;
    gap: 0.65rem;
  }

  .history-heading h2 {
    margin: 0;
    font-size: 1.15rem;
  }

  .history-heading span,
  .history-meta span,
  .history-meta small {
    color: #777777;
  }

  .history-refresh {
    min-height: 38px;
    padding: 0.55rem 0.8rem;
  }

  .history-date-list {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .history-date-group {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .history-date-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem;
  }

  .history-date-toggle {
    width: 100%;
    min-height: 54px;
    padding: 0.8rem 0.15rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border: 0;
    background: transparent;
    color: #303030;
    text-align: left;
  }

  .history-date-toggle:hover {
    color: #FE6A37;
  }

  .history-date-state {
    min-width: 56px;
    min-height: 34px;
    padding: 0.4rem 0.7rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    flex: 0 0 auto;
    border: 1px solid #FE6A37;
    border-radius: 6px;
    background: #ffffff;
    color: #FE6A37;
    font-size: 0.86rem;
    font-weight: 800;
  }

  .history-date-state.expanded,
  .history-date-toggle:hover .history-date-state {
    background: #FE6A37;
    color: #ffffff;
  }

  .history-date-label {
    min-width: 0;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.65rem;
  }

  .history-date-label small {
    color: #777777;
    font-weight: 700;
  }

  .history-date-download {
    white-space: nowrap;
  }

  .history-list {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-left: 1rem;
  }

  .history-batch {
    padding: 1rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .history-meta {
    min-width: 0;
    padding-left: 4rem;
    display: grid;
    grid-template-columns: auto auto;
    align-items: baseline;
    gap: 0.25rem 0.75rem;
  }

  .history-meta strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .history-meta small {
    grid-column: 1 / -1;
  }

  .history-actions {
    display: flex;
    flex: 0 0 auto;
    gap: 0.55rem;
  }

  .history-result-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
  }

  .history-date-state,
  .history-date-download,
  .history-result-toggle {
    min-height: 34px;
    height: 34px;
    padding: 0.4rem 0.7rem;
    border-radius: 6px;
    font-size: 0.86rem;
    font-weight: 800;
    line-height: 1.2;
    white-space: nowrap;
  }

  .history-empty {
    margin: 0;
    padding: 1rem 0;
    color: #777777;
    text-align: center;
  }

  .history-results {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .results-shell {
    display: grid;
    gap: 0.75rem;
    overflow-x: auto;
    padding-bottom: 0.25rem;
  }

  .result-header {
    display: grid;
    grid-template-columns: repeat(3, minmax(190px, 1fr));
    gap: 0.75rem;
    width: min(980px, 100%);
    min-width: 680px;
    margin: 0 auto;
    color: #666666;
    font-weight: 800;
    padding: 0 0.15rem;
    text-align: center;
  }

  .result-scroll {
    display: grid;
    gap: 1rem;
    overflow: visible;
  }

  .result-row {
    width: min(980px, 100%);
    min-width: 680px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3, minmax(190px, 1fr));
    gap: 0.75rem;
    padding: 0.35rem 0 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .result-row:last-child {
    border-bottom: none;
  }

  .image-cell {
    margin: 0;
    display: grid;
    grid-template-rows: auto auto;
    gap: 0.45rem;
    align-content: start;
    min-height: 0;
    border-radius: 0;
    overflow: visible;
    background: transparent;
    min-width: 0;
  }

  .image-cell img {
    width: 100%;
    height: clamp(150px, 18vw, 230px);
    min-width: 0;
    object-fit: contain;
    display: block;
    background: transparent;
  }

  .image-cell figcaption {
    min-height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: transparent;
    color: #404040;
  }

  .image-cell figcaption.original-caption {
    flex-direction: column;
    gap: 0.1rem;
    justify-content: center;
    text-align: center;
  }

  .image-cell figcaption span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 800;
  }

  .image-cell figcaption small {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    color: #888888;
    font-size: 0.78rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .image-cell button {
    min-height: 34px;
    padding: 0.45rem 0.65rem;
    font-size: 0.86rem;
    flex: 0 0 auto;
  }

  .image-cell .image-preview-trigger {
    width: 100%;
    min-height: 0;
    padding: 0;
    display: block;
    flex: initial;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: inherit;
    cursor: zoom-in;
    overflow: hidden;
  }

  .image-cell .image-preview-trigger:hover {
    background: transparent;
    color: inherit;
  }

  .image-cell .image-preview-trigger:focus-visible {
    outline: 2px solid #FE6A37;
    outline-offset: 2px;
  }

  .placeholder-cell {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
    justify-content: center;
    height: clamp(150px, 18vw, 230px);
    border: 1px dashed rgba(254, 106, 55, 0.3);
    border-radius: 8px;
    background: rgba(254, 106, 55, 0.04);
    color: #666666;
    font-weight: 800;
  }

  .placeholder-cell.failed {
    color: #d94747;
  }

  .loading-spinner {
    width: 34px;
    height: 34px;
    border: 3px solid rgba(254, 106, 55, 0.22);
    border-top-color: #FE6A37;
    border-radius: 50%;
    animation: cockerSpin 0.9s linear infinite;
  }

  @keyframes cockerSpin {
    to {
      transform: rotate(360deg);
    }
  }

  :global(.dark) .gate-page {
    background:
      linear-gradient(180deg, rgba(18, 18, 18, 0.9), rgba(20, 23, 28, 0.96)),
      url('/images/hero/hero-dog.png') center/cover;
  }

  :global(.dark) .gate-panel,
  :global(.dark) .password-modal,
  :global(.dark) .name-row {
    background: #1f242d;
    color: #f5f7fa;
    border-color: rgba(255, 255, 255, 0.1);
  }

  :global(.dark) .cocker-page,
  :global(.dark) .gate-panel h1,
  :global(.dark) .page-top h1,
  :global(.dark) .modal-heading h2,
  :global(.dark) .progress-label strong {
    color: #f5f7fa;
  }

  :global(.dark) .gate-input,
  :global(.dark) .password-modal input,
  :global(.dark) .name-row input,
  :global(.dark) .name-row select {
    background: #161a21;
    border-color: rgba(255, 255, 255, 0.12);
    color: #f5f7fa;
  }

  :global(.dark) .upload-zone {
    background: #171c24;
  }

  :global(.dark) .workspace-tabs {
    border-color: rgba(255, 255, 255, 0.1);
  }

  :global(.dark) .workspace-tabs button {
    color: #aeb5c0;
  }

  :global(.dark) .workspace-tabs button:hover,
  :global(.dark) .workspace-tabs button.active {
    color: #FE6A37;
  }

  :global(.dark) .style-card {
    border-color: rgba(255, 255, 255, 0.1);
    background: #1f242d;
    color: #d4d9e0;
  }

  :global(.dark) .style-card:hover,
  :global(.dark) .style-card:focus-visible {
    color: #FE6A37;
  }

  :global(.dark) .style-card:hover {
    border-color: rgba(254, 106, 55, 0.65);
  }

  :global(.dark) .style-card.selected {
    border-color: #FE6A37;
    background: #1f242d;
    color: #FE6A37;
  }

  :global(.dark) .example-tile {
    background: #171c24;
    color: #aeb5c0;
  }

  :global(.dark) .placeholder-cell {
    background: #171c24;
  }

  :global(.dark) .image-cell figcaption {
    color: #f5f7fa;
  }

  :global(.dark) .history-section,
  :global(.dark) .history-date-list,
  :global(.dark) .history-date-group,
  :global(.dark) .history-list,
  :global(.dark) .history-batch,
  :global(.dark) .history-results {
    border-color: rgba(255, 255, 255, 0.1);
  }

  :global(.dark) .history-heading span,
  :global(.dark) .history-date-label small,
  :global(.dark) .history-meta span,
  :global(.dark) .history-meta small,
  :global(.dark) .history-empty {
    color: #aeb5c0;
  }

  :global(.dark) .history-date-toggle {
    color: #f5f7fa;
  }

  :global(.dark) .history-date-state {
    background: #1f242d;
  }

  :global(.dark) .history-date-state.expanded,
  :global(.dark) .history-date-toggle:hover .history-date-state {
    background: #FE6A37;
  }

  @media (max-width: 760px) {
    .cocker-page {
      padding: 0.9rem;
    }

    .image-preview-backdrop {
      padding: 4.25rem 1rem 1.25rem;
    }

    .image-preview-modal {
      width: calc(100vw - 2rem);
      height: min(760px, 78vh);
      height: min(760px, 78dvh);
    }

    .page-top,
    .control-band {
      grid-template-columns: 1fr;
      align-items: stretch;
    }

    .modal-actions {
      display: grid;
      grid-template-columns: 1fr;
    }

    .page-top {
      display: grid;
    }

    .style-grid {
      grid-template-columns: repeat(5, minmax(150px, 1fr));
      gap: 0.75rem;
      overflow-x: auto;
      padding-bottom: 0.5rem;
      scroll-snap-type: x proximity;
    }

    .style-card {
      scroll-snap-align: start;
    }

    .name-list {
      grid-template-columns: 1fr;
    }

    .name-row {
      grid-template-columns: 72px minmax(0, 1fr) 32px;
      gap: 0.55rem;
    }

    .upload-thumb {
      width: 72px;
      height: 72px;
    }

    .pet-meta-grid {
      grid-template-columns: 1fr;
    }

    .result-header,
    .result-row {
      min-width: 620px;
    }

    .download-all-row {
      justify-content: stretch;
    }

    .history-summary {
      align-items: stretch;
      flex-direction: column;
    }

    .history-meta {
      grid-template-columns: 1fr;
    }

    .history-meta small {
      grid-column: auto;
    }

    .history-actions {
      justify-content: flex-end;
    }

    .history-meta {
      padding-left: 1.1rem;
    }

    .history-list {
      padding-left: 0.5rem;
    }

    .history-date-header {
      grid-template-columns: 1fr;
      gap: 0;
      padding-bottom: 0.75rem;
    }

    .history-date-download {
      justify-self: end;
    }
  }
</style>
